import React, { useEffect, useState, useRef } from 'react';
import axios from '../axiosInstance';
import { motion } from 'framer-motion';
import { FiX } from 'react-icons/fi';

export default function CommodityStockList() {
  const [inventory, setInventory] = useState([]);

  // State filter states
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [stateSearch, setStateSearch] = useState('');
  const [stateDropdownOpen, setStateDropdownOpen] = useState(false);

  // Commodity filter states
  const [commodities, setCommodities] = useState([]);
  const [selectedCommodity, setSelectedCommodity] = useState('');
  const [commoditySearch, setCommoditySearch] = useState('');
  const [commodityDropdownOpen, setCommodityDropdownOpen] = useState(false);

  // Scheme filter states
  const [schemes, setSchemes] = useState([]);
  const [selectedScheme, setSelectedScheme] = useState('');
  const [schemeSearch, setSchemeSearch] = useState('');
  const [schemeDropdownOpen, setSchemeDropdownOpen] = useState(false);

  // Season filter states
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState('');
  const [seasonSearch, setSeasonSearch] = useState('');
  const [seasonDropdownOpen, setSeasonDropdownOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);

  const token = localStorage.getItem('token');

  // User role and state for branch users
  const [role, setRole] = useState('');
  const [userStateId, setUserStateId] = useState('');

  // Fetch inventory
  const fetchInventory = async (
    pageNum = 1,
    limitNum = 10,
    stateId = '',
    commodityId = '',
    schemeId = '',
    seasonId = ''
  ) => {
    setLoading(true);
    try {
      let url = `inventory/commodity-inventory?page=${pageNum}&limit=${limitNum}`;
      if (stateId) url += `&state_id=${stateId}`;
      if (commodityId) url += `&commodity_id=${commodityId}`;
      if (schemeId) url += `&scheme_id=${schemeId}`;
      if (seasonId) url += `&season_id=${seasonId}`;

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setInventory(res.data.data || []);
      setTotal(res.data.total || res.data.data?.length || 0);
      setError('');
    } catch (err) {
      console.error(
        'Error fetching inventory:',
        err.response?.data || err.message
      );
      setError('⚠️ Failed to fetch commodity inventory.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch commodities
  const fetchCommodities = async () => {
    try {
      const res = await axios.get('commodity', {
        headers: { Authorization: `Bearer ${token}` },
      });
      // console.log(res);
      setCommodities(res.data || []);
    } catch (err) {
      console.error('Error fetching commodities:', err.message);
    }
  };

  // Fetch schemes
  const fetchSchemes = async () => {
    try {
      const res = await axios.get('schemes', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSchemes(res.data || []);
    } catch (err) {
      console.error('Error fetching schemes:', err.message);
    }
  };

  // Fetch seasons
  const fetchSeasons = async () => {
    try {
      const res = await axios.get('seasons?paginate=false', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSeasons(res.data || []);
    } catch (err) {
      console.error('Error fetching seasons:', err.message);
    }
  };

  // Fetch states
  const fetchStates = async () => {
    try {
      const res = await axios.get('states', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStates(res.data || []);
    } catch (err) {
      console.error('Error fetching states:', err.message);
    }
  };

  // Filter states with search text
  const filteredStates = states.filter((s) =>
    (s?.name || '').toLowerCase().includes(stateSearch.toLowerCase())
  );

  // Filter commodities with search text
  const filteredCommodities = commodities.filter((c) =>
    (c?.commodityname || '')
      .toLowerCase()
      .includes(commoditySearch.toLowerCase())
  );

  // Filter schemes with search text
  const filteredSchemes = schemes.filter((s) =>
    (s?.name || '').toLowerCase().includes(schemeSearch.toLowerCase())
  );

  // Filter seasons with search text
  const filteredSeasons = seasons.filter((s) =>
    (s?.name || '').toLowerCase().includes(seasonSearch.toLowerCase())
  );

  // Clear all filters
  const clearFilters = () => {
    setSelectedState('');
    setStateSearch('');
    setSelectedCommodity('');
    setCommoditySearch('');
    setSelectedScheme('');
    setSchemeSearch('');
    setSelectedSeason('');
    setSeasonSearch('');
    setPage(1);
  };

  // Ref to track if initial data has been fetched
  const initialDataFetchedRef = useRef(false);

  useEffect(() => {
    if (!initialDataFetchedRef.current) {
      initialDataFetchedRef.current = true;
      // Don't call fetchInventory here as it will be called by the dependency-based useEffect
      fetchStates();
      fetchCommodities();
      fetchSchemes();
      fetchSeasons();

      // Get user role and state from localStorage
      const user = JSON.parse(localStorage.getItem('user'));
      const userRole = user?.roles?.[0]?.name || '';
      const stateId = user?.state?.id || '';
      const stateName = user?.state?.name || '';
      setRole(userRole);
      setUserStateId(stateId);

      // Auto-set state filter for Branch users
      if (userRole === 'Branch' && stateId) {
        setSelectedState(stateId);
        setStateSearch(stateName);
      }
    }
  }, []);

  // Ref to prevent duplicate API calls when filters change
  const fetchDataRef = useRef(null);

  useEffect(() => {
    // Clear any pending timeouts
    if (fetchDataRef.current) {
      clearTimeout(fetchDataRef.current);
    }

    // Set a small timeout to debounce the API call
    fetchDataRef.current = setTimeout(() => {
      fetchInventory(
        page,
        limit,
        selectedState,
        selectedCommodity,
        selectedScheme,
        selectedSeason
      );
    }, 100);

    // Cleanup function to clear timeout
    return () => {
      if (fetchDataRef.current) {
        clearTimeout(fetchDataRef.current);
      }
    };
  }, [page, selectedState, selectedCommodity, selectedScheme, selectedSeason]);

  const totalPages = Math.ceil(total / limit);

  // Export commodity inventory to Excel
  const handleExport = async () => {
    try {
      setLoading(true);

      // Build query parameters based on current filters
      let queryParams = new URLSearchParams();
      if (selectedState) queryParams.append('state_id', selectedState);
      if (selectedCommodity)
        queryParams.append('commodity_id', selectedCommodity);
      if (selectedScheme) queryParams.append('scheme_id', selectedScheme);
      if (selectedSeason) queryParams.append('season_id', selectedSeason);

      // Make API call to export endpoint
      const res = await axios.get(
        `inventory/export-commoditywise-excel?${queryParams.toString()}`,
        {
          responseType: 'blob',
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `commodity-inventory-export-${new Date().toISOString().split('T')[0]
        }.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();

      alert('✅ Export successful');
    } catch (err) {
      console.error('Export failed:', err);
      alert('⚠️ Export failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-blue-100 p-10  text-gray-900">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 md:mb-10 gap-4">
        <h2 className="text-3xl md:text-4xl font-extrabold text-blue-800 tracking-tight">
          📦 Commodity Wise Stock
        </h2>
      </div>

      {/* Filter and Actions */}
      <div className="flex flex-wrap gap-4 mb-6">
        {/* State Dropdown */}
        <div className="relative w-[250px] max-w-full">
          <div
            onClick={() => {
              // Close all other dropdowns first
              setCommodityDropdownOpen(false);
              setSchemeDropdownOpen(false);
              setSeasonDropdownOpen(false);
              setStateDropdownOpen((prev) => !prev);
            }}
            className="px-4 py-2 border rounded-xl bg-white shadow-sm cursor-pointer text-base flex justify-between items-center h-[50px]"
          >
            <span>
              {selectedState ? stateSearch || 'State Selected' : 'Select State'}
            </span>
            <div className="flex items-center gap-2">
              {selectedState && (
                <FiX
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedState('');
                    setStateSearch('');
                    setPage(1);
                  }}
                  className="text-gray-400 hover:text-red-500 cursor-pointer"
                  size={18}
                />
              )}
              <span className="text-gray-500">
                {stateDropdownOpen ? '▲' : '▼'}
              </span>
            </div>
          </div>
          {stateDropdownOpen && (
            <div className="absolute top-full left-0 right-0 bg-white border rounded-xl shadow-lg max-h-72 overflow-y-auto mt-1 z-10">
              <input
                type="text"
                placeholder="Search State..."
                value={stateSearch}
                onChange={(e) => setStateSearch(e.target.value)}
                className="w-full px-3 py-2 border-b text-sm focus:outline-none"
              />
              {filteredStates.length === 0 && (
                <p className="p-2 text-gray-500">No states found</p>
              )}
              {filteredStates.map((s) => (
                <div
                  key={s.id}
                  onClick={() => {
                    setSelectedState(s.id);
                    setStateSearch(s.name);
                    setStateDropdownOpen(false);
                    setPage(1);
                  }}
                  className={`p-2 cursor-pointer hover:bg-blue-100 ${selectedState === s.id ? 'bg-blue-200 font-semibold' : ''
                    }`}
                >
                  {s.name}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Commodity Dropdown */}
        <div className="relative w-[250px] max-w-full">
          <div
            onClick={() => {
              // Close all other dropdowns first
              setStateDropdownOpen(false);
              setSchemeDropdownOpen(false);
              setSeasonDropdownOpen(false);
              setCommodityDropdownOpen((prev) => !prev);
            }}
            className="px-4 py-2 border rounded-xl bg-white shadow-sm cursor-pointer text-base flex justify-between items-center h-[50px]"
          >
            <span>
              {selectedCommodity
                ? commoditySearch || 'Commodity Selected'
                : 'Select Commodity'}
            </span>
            <div className="flex items-center gap-2">
              {selectedCommodity && (
                <FiX
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedCommodity('');
                    setCommoditySearch('');
                    setPage(1);
                  }}
                  className="text-gray-400 hover:text-red-500 cursor-pointer"
                  size={18}
                />
              )}
              <span className="text-gray-500">
                {commodityDropdownOpen ? '▲' : '▼'}
              </span>
            </div>
          </div>
          {commodityDropdownOpen && (
            <div className="absolute top-full left-0 right-0 bg-white border rounded-xl shadow-lg max-h-72 overflow-y-auto mt-1 z-10">
              <input
                type="text"
                placeholder="Search Commodity..."
                value={commoditySearch}
                onChange={(e) => setCommoditySearch(e.target.value)}
                className="w-full px-3 py-2 border-b text-sm focus:outline-none"
              />
              {filteredCommodities.length === 0 && (
                <p className="p-2 text-gray-500">No commodities found</p>
              )}
              {filteredCommodities.map((c) => (
                <div
                  key={c.id}
                  onClick={() => {
                    setSelectedCommodity(c.id);
                    setCommoditySearch(c.commodityname);
                    setCommodityDropdownOpen(false);
                    setPage(1);
                  }}
                  className={`p-2 cursor-pointer hover:bg-blue-100 ${selectedCommodity === c.id
                    ? 'bg-blue-200 font-semibold'
                    : ''
                    }`}
                >
                  {c.commodityname}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Scheme Dropdown */}
        <div className="relative w-[250px] max-w-full">
          <div
            onClick={() => {
              // Close all other dropdowns first
              setStateDropdownOpen(false);
              setCommodityDropdownOpen(false);
              setSeasonDropdownOpen(false);
              setSchemeDropdownOpen((prev) => !prev);
            }}
            className="px-4 py-2 border rounded-xl bg-white shadow-sm cursor-pointer text-base flex justify-between items-center h-[50px]"
          >
            <span>
              {selectedScheme
                ? schemeSearch || 'Scheme Selected'
                : 'Select Scheme'}
            </span>
            <div className="flex items-center gap-2">
              {selectedScheme && (
                <FiX
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedScheme('');
                    setSchemeSearch('');
                    setPage(1);
                  }}
                  className="text-gray-400 hover:text-red-500 cursor-pointer"
                  size={18}
                />
              )}
              <span className="text-gray-500">
                {schemeDropdownOpen ? '▲' : '▼'}
              </span>
            </div>
          </div>
          {schemeDropdownOpen && (
            <div className="absolute top-full left-0 right-0 bg-white border rounded-xl shadow-lg max-h-72 overflow-y-auto mt-1 z-10">
              <input
                type="text"
                placeholder="Search Scheme..."
                value={schemeSearch}
                onChange={(e) => setSchemeSearch(e.target.value)}
                className="w-full px-3 py-2 border-b text-sm focus:outline-none"
              />
              {filteredSchemes.length === 0 && (
                <p className="p-2 text-gray-500">No schemes found</p>
              )}
              {filteredSchemes.map((s) => (
                <div
                  key={s.id}
                  onClick={() => {
                    setSelectedScheme(s.id);
                    setSchemeSearch(s.name);
                    setSchemeDropdownOpen(false);
                    setPage(1);
                  }}
                  className={`p-2 cursor-pointer hover:bg-blue-100 ${selectedScheme === s.id ? 'bg-blue-200 font-semibold' : ''
                    }`}
                >
                  {s.name}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Season Dropdown */}
        <div className="relative w-[250px] max-w-full">
          <div
            onClick={() => {
              // Close all other dropdowns first
              setStateDropdownOpen(false);
              setCommodityDropdownOpen(false);
              setSchemeDropdownOpen(false);
              setSeasonDropdownOpen((prev) => !prev);
            }}
            className="px-4 py-2 border rounded-xl bg-white shadow-sm cursor-pointer text-base flex justify-between items-center h-[50px]"
          >
            <span>
              {selectedSeason
                ? seasonSearch || 'Season Selected'
                : 'Select Season'}
            </span>
            <div className="flex items-center gap-2">
              {selectedSeason && (
                <FiX
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedSeason('');
                    setSeasonSearch('');
                    setPage(1);
                  }}
                  className="text-gray-400 hover:text-red-500 cursor-pointer"
                  size={18}
                />
              )}
              <span className="text-gray-500">
                {seasonDropdownOpen ? '▲' : '▼'}
              </span>
            </div>
          </div>
          {seasonDropdownOpen && (
            <div className="absolute top-full left-0 right-0 bg-white border rounded-xl shadow-lg max-h-72 overflow-y-auto mt-1 z-10">
              <input
                type="text"
                placeholder="Search Season..."
                value={seasonSearch}
                onChange={(e) => setSeasonSearch(e.target.value)}
                className="w-full px-3 py-2 border-b text-sm focus:outline-none"
              />
              {filteredSeasons.length === 0 && (
                <p className="p-2 text-gray-500">No seasons found</p>
              )}
              {filteredSeasons.map((s) => (
                <div
                  key={s.id}
                  onClick={() => {
                    setSelectedSeason(s.id);
                    setSeasonSearch(s.name);
                    setSeasonDropdownOpen(false);
                    setPage(1);
                  }}
                  className={`p-2 cursor-pointer hover:bg-blue-100 ${selectedSeason === s.id ? 'bg-blue-200 font-semibold' : ''
                    }`}
                >
                  {s.name}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <button
            onClick={clearFilters}
            className="bg-gray-500 text-white px-4 py-2 rounded-lg shadow-md hover:bg-gray-600 flex items-center gap-1"
          >
            🔄 Clear Filters
          </button>

          <button
            onClick={handleExport}
            disabled={loading}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg shadow-md hover:bg-purple-700 disabled:bg-purple-400 disabled:cursor-not-allowed flex items-center gap-1"
          >
            {loading ? 'Exporting...' : '📥 Export'}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 text-lg p-4 rounded mb-6 shadow">
          {error}
        </div>
      )}

      {/* Table */}
      <motion.div className="overflow-hidden rounded-2xl shadow-xl border border-gray-100 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead className="bg-gray-100 border-b border-gray-200">
              <tr>
                {[
                  'State',
                  'Commodity',
                  'Scheme',
                  'Season',
                  'Qty(MT)',
                  'Total Bags',
                  'Age of Stock',
                ].map((header) => (
                  <th
                    key={header}
                    className="py-4 px-6 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan="9" className="text-center py-10 text-blue-600 font-semibold">
                    ⏳ Loading inventory...
                  </td>
                </tr>
              ) : inventory.length > 0 ? (
                inventory.map((inv, index) => (
                  <motion.tr
                    key={index}
                    className="hover:bg-gray-50 transition-colors duration-200"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <td className="py-4 px-6 text-sm text-gray-700 font-medium">
                      {inv.state_name}
                    </td>
                    <td className="py-4 px-6 text-sm text-blue-700 font-medium">
                      {inv.commodity_name}
                    </td>
                    <td className="py-4 px-6 text-sm">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                        {inv.scheme_name}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-700">
                      {inv.season_name}
                    </td>

                    <td className="py-4 px-6 text-sm">
                      <div className="flex flex-col gap-1">
                        <div className="whitespace-nowrap"><span className="text-gray-500 text-xs">Total:</span> <span className="inline-block px-2 py-0.5 rounded-lg bg-green-50 text-green-700 font-bold border border-green-100">{inv.total_quantity}</span></div>
                        <div className="whitespace-nowrap"><span className="text-gray-500 text-xs">Blocked:</span> <span className="inline-block px-2 py-0.5 rounded-lg bg-red-50 text-red-700 font-bold border border-red-100">{inv.total_blocked_quantity || '0.0000'}</span></div>
                        <div className="whitespace-nowrap"><span className="text-gray-500 text-xs">Current:</span> <span className="inline-block px-2 py-0.5 rounded-lg bg-blue-50 text-blue-700 font-bold border border-blue-100"> {inv.total_current_quantity || '0.0000'}</span></div>
                        <div className="whitespace-nowrap"><span className="text-gray-500 text-xs">Withdrawn:</span> <span className="inline-block px-2 py-0.5 rounded-lg bg-gray-50 text-gray-700 font-bold border border-gray-100"> {inv.total_withdrawn_quantity || '0.0000'}</span></div>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-sm">
                      <div className="flex flex-col gap-1">
                        <div className="whitespace-nowrap"><span className="text-gray-500 text-xs">Total:</span> <span className="text-gray-700 font-medium">{inv.total_bags}</span></div>
                        <div className="whitespace-nowrap"><span className="text-gray-500 text-xs">Blocked:</span> <span className="inline-block px-2 py-0.5 rounded-lg bg-red-50 text-red-700 font-bold border border-red-100">{inv.total_blocked_bags}</span></div>
                        <div className="whitespace-nowrap"><span className="text-gray-500 text-xs">Current:</span> <span className="inline-block px-2 py-0.5 rounded-lg bg-blue-50 text-blue-700 font-bold border border-blue-100">{inv.total_current_bags}</span></div>
                        <div className="whitespace-nowrap"><span className="text-gray-500 text-xs">Withdrawn:</span> <span className="inline-block px-2 py-0.5 rounded-lg bg-gray-50 text-gray-700 font-bold border border-gray-100">{inv.total_withdrawal_bags}</span></div>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-700">
                      {inv.age_of_stock}
                    </td>
                  </motion.tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="text-center py-12 text-gray-500 font-medium">
                    🚫 No inventory found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Pagination */}
      <div className="flex justify-center mt-6 gap-2 md:gap-3 items-center text-sm md:text-base">
        <button
          onClick={() => page > 1 && setPage(page - 1)}
          disabled={page === 1}
          className="px-3 md:px-4 py-1 md:py-2 bg-gray-200 hover:bg-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Prev
        </button>
        <span>
          Page {page} of {totalPages || 1}
        </span>
        <button
          onClick={() => page < totalPages && setPage(page + 1)}
          disabled={page === totalPages}
          className="px-3 md:px-4 py-1 md:py-2 bg-gray-200 hover:bg-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    </div>
  );
}
